import { useEffect, useMemo, useRef } from 'react';
import { throttle } from 'lodash-es';
import { Box } from '@alf/uikit';
import { useIntersect } from '@namespace/helpers';

import styles from './index.module.css';

export const InfiniteLoader = ({
  Component = Box,
  className,
  onLoadMore,
  isLoading = false,
  bottomOffset = 30,
  strobeInterval = 500,

  children
}) => {
  const rootRef = useRef();
  const [setBeaconRef, { isIntersecting }] = useIntersect({
    root: rootRef.current
  });
  // We need to use refs because throttled function at the end of a timeout will have stale variables, specifically - isAtBottom and onLoadMore.
  const isAtBottomRef = useRef(false);
  const loadMoreRef = useRef(onLoadMore);
  const isLoadingRef = useRef(isLoading);

  useEffect(() => {
    loadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  const loaderFn = useMemo(
    () =>
      // We postpone consecutive updates for at least 500ms so a browser had time to render results and reflect actual 'isAtBottom' state at the moment we call next load.
      throttle(() => {
        if (isAtBottomRef.current && !isLoadingRef.current) {
          loadMoreRef.current();
        }
      }, strobeInterval),
    [strobeInterval]
  );

  useEffect(() => {
    isAtBottomRef.current = isIntersecting;
    loaderFn();
  }, [isIntersecting, loaderFn, isLoading]); // 'isLoading' dep is important here - it's a trigger for consecutive loads at the exact moment when previous load finishes

  return (
    <Component className={className} ref={rootRef}>
      {children}
      <div
        className={styles.beacon}
        ref={setBeaconRef}
        style={{
          bottom: `${bottomOffset}px`
        }}
      />
    </Component>
  );
};
